import { Grid, LinearProgress, Stack, Typography } from "@mui/material";
import { omit } from "lodash";
import type { FC } from "react";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import AsyncLoadingButton from "../../components/asyncLoadingButton/AsyncLoadingButton";
import CancelButton from "../../components/cancelButton/CancelButton";
import { useQuery } from "../../misc/useQuery";
import type { ProductResponse } from "../products/productsApi";
import { useProducts } from "../products/useProducts";
import type { PackageDayDescriptionType } from "./PackageDescriptionTypes";
import type { Package } from "./PackageEdit";
import PackageEdit from "./PackageEdit";
import type { PackageResponse } from "./packagesApi";
import { toPackageTagRequest } from "./packageTagRequest";
import { useNextPackageUserFriendlyId, usePackageUserFriendlyId } from "./packageUserFriendlyId";
import { useCreatePackage } from "./useCreatePackage";
import { usePackageWithId } from "./usePackageWithId";

const newPackage: (userFriendlyId: string) => Package = (userFriendlyId: string) => ({
  userFriendlyId,
  name: "",
  wwwName: "",
  currency: "PLN",
  margin: 0,
  note: "",
  description: "",
  detailsDict: {} as Record<PackageDayDescriptionType, string | null>,
  days: [],
  tags: [],
  images: [],
  isPublic: false,
  isPublicDayDesc: {},
  regionId: null,
  parentPackageId: null,
});

function clone(pkg: PackageResponse, userFriendlyId: string): Package {
  return omit(
    {
      ...pkg,
      userFriendlyId: userFriendlyId,
      name: `${pkg.name.value} (kopia)`,
      wwwName: pkg.name.publicValue ? `${pkg.name.publicValue} (kopia)` : "",
      note: pkg.note ?? "",
      images: pkg.media.images,
      map: pkg.media.map,
      tags: pkg.tags ?? [],
      days: pkg.days.map((d) => ({ ...d, title: d.title ?? "" })),
      isPublic: pkg.isPublic ?? false,
      isPublicDayDesc: pkg.isPublicDayDesc ?? {},
      parentPackageId: pkg.parentPackageId ?? null,
    },
    "media",
  );
}

interface Props {
  readonly initialPackage: Package;
  readonly products: ProductResponse[];
}

const NewPackagePage: FC<Props> = ({ initialPackage, products }) => {
  const navigate = useNavigate();
  const { createPackage } = useCreatePackage();
  const [errors, setErrors] = useState<string[]>([]);
  const { control, handleSubmit, watch, setValue, getValues } = useForm<Package>({
    defaultValues: initialPackage,
    mode: "onBlur",
  });

  const regionId = watch("regionId");
  const parentPackageId = watch("parentPackageId");
  const currentPackageId = watch("userFriendlyId");

  const {
    prefix,
    validate: validateId,
    isLoading: isValidationLoading,
    nextUserFriendlyId,
  } = usePackageUserFriendlyId(parentPackageId, regionId, currentPackageId);

  useEffect(() => {
    // Nie ustawiaj ID, zanim dane potrzebne do generowania się załadują.
    if (isValidationLoading) {
      return;
    }

    // Przelicz ID po zmianie rodzica/regionu.
    setValue("userFriendlyId", nextUserFriendlyId, {
      shouldDirty: true,
      shouldValidate: true,
    });
  }, [parentPackageId, regionId, nextUserFriendlyId, isValidationLoading, setValue, getValues]);
  useEffect(() => {
    const subscription = watch(() => setErrors((s) => (s.length === 0 ? s : [])));
    return () => subscription.unsubscribe();
  }, [watch]);

  const getProductCurrency = (productId: string) => products?.find((p) => p.id === productId)?.currency;

  const submitPackage = async (state: Package) => {
    const productsWithDifferentCurrency = state.days
      .flatMap((d) => d.products)
      .map((p) => p.id)
      .filter((id) => getProductCurrency(id) !== state.currency);
    if (productsWithDifferentCurrency.length > 0) {
      setErrors(productsWithDifferentCurrency);
    } else {
      const { id } = await createPackage({
        userFriendlyId: state.userFriendlyId,
        name: { value: state.name, publicValue: state.wwwName ? state.wwwName : undefined },
        note: state.note || undefined,
        description: state.description || undefined,
        currency: state.currency,
        margin: state.margin,
        detailsDict: state.detailsDict,
        days: state.days.map((d) => ({
          title: d.title,
          details: d.details,
          dayNumber: d.dayNumber,
          products: d.products.map((p) => ({
            id: p.id,
            optional: p.optional ?? false,
            options: p.options.filter((o): o is string => o !== null),
          })),
          image: d.image,
        })),
        tags: toPackageTagRequest(state.tags),
        media: {
          images: state.images,
          map: state.map,
        },
        isPublic: state.isPublic,
        isPublicDayDesc: state.isPublicDayDesc,
        regionId: state.regionId,
        parentPackageId: state.parentPackageId,
      });
      navigate(`/admin/packages/${id.id}`);
    }
  };

  return (
    <Grid
      container
      spacing={2}
      sx={{
        marginTop: 1,
        marginBottom: 1,
        paddingLeft: 1,
        paddingRight: 1,
      }}
    >
      <PackageEdit control={control} errors={errors} validateId={validateId} userFriendlyIdPrefix={prefix} />
      <Grid
        size={12}
        container
        sx={{
          justifyContent: "flex-end",
        }}
      >
        <Stack direction="row" spacing={2}>
          <CancelButton onClick={() => navigate("/admin/packages")} />
          <AsyncLoadingButton onClick={handleSubmit(submitPackage)}>Zapisz</AsyncLoadingButton>
        </Stack>
      </Grid>
    </Grid>
  );
};

const InitializedNewPackagePage = () => {
  const { products, isLoading: areProductsLoading } = useProducts();
  const query = useQuery();
  const [state, setState] = useState<Package>();
  const fromPackageId = query.get("from");
  const { data: existingPackage, isLoading: isPackageLoading } = usePackageWithId(fromPackageId ?? undefined);
  const { isLoading: isUserFriendlyIdLoading, nextUserFriendlyId } = useNextPackageUserFriendlyId(
    state?.regionId,
    state?.parentPackageId,
  );

  const isLoading = isPackageLoading || isUserFriendlyIdLoading;

  useEffect(() => {
    if (!isLoading && fromPackageId && existingPackage) {
      setState(clone(existingPackage, nextUserFriendlyId));
    }
    if (!fromPackageId) {
      setState(newPackage(nextUserFriendlyId));
    }
  }, [fromPackageId, existingPackage, isLoading]);

  if (areProductsLoading || isLoading) {
    return (
      <Grid container>
        <Grid size={12}>
          <LinearProgress data-testid="loading-indicator" />
        </Grid>
      </Grid>
    );
  }
  if (!isLoading && !state) {
    return (
      <Grid container>
        <Grid size={12}>
          <Typography variant="h5">Taki pakiet nie istnieje</Typography>
        </Grid>
      </Grid>
    );
  }

  return <NewPackagePage initialPackage={state!} products={products} />;
};

export default InitializedNewPackagePage;
